// ============================================================================
// CANCUN100 — SalaPreciosTab.js  (Bloque 18: máscara COMPLETA de Sala de Ventas)
// ----------------------------------------------------------------------------
// Clon de TopToursPreciosTab con la misma UX, pero SIN over: el precio es el de la biblia salvo que
// Marco o Tere lo editen.
// LECTURA: vista sala_lista (productos de la biblia + temporales TMS-…; precio_adulto/menor = editado o biblia,
//          precio_biblia_*, precio_neto_*, activo_sala, ⭐/orden, es_promocion, es_temporal, temporal_id…).
// ESCRITURA: producto de biblia → upsert por sku en sala_precios (precio_publico_* null = el de la biblia);
//            temporal → update por temporal_id en sala_temporales; alta en sala_temporales;
//            ligar → rpc sala_ligar_temporal. NUNCA .delete(): apagar = activo=false.
// Roles: editores Marco (super_admin) y Tere (contabilidad); Polo solo lectura; agentes de Sala ven
// solo lo prendido (activo_sala). Nadie edita netos. Pendientes de biblia: solo Marco (como en Top Tours).
// La regla de comisión de Sala NO cambia (40% / RP 80%, 5% con /1.05): aquí solo hay precios.
// ============================================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { lineaContacto } from './contacto';
import DescriptivoBoton from './descriptivos/DescriptivoBoton';
import { RADIO, RADIO_TARJETA, RADIO_INSIGNIA } from './tema';
import Segmentado from './Segmentado';
import Stepper from './Stepper'; // APP 2/05 · F1
import useBorrador, { claveBorrador, BandaBorrador } from './useBorrador'; // Bloque 27
import { COLS_AGENTE, COLS_ADMIN, ALTO_FILA, TablaPareja, CeldaNota, GloboNota, FilaMovil, DatoMovil, useMovil, tdP, numP, clamp2, linea1 } from './ListaPareja'; // APP 2/28 (RP-10)
import { Ic } from './iconos'; // APP 2/34: íconos Lucide en vez de emoji
const INICIAL_TMP = { proveedor: '', otro: '', nombre: '', moneda: '', nacional: null, tipo: 'pax', cubreAd: '2', cubreMe: '0', netoAd: '', netoMe: '', pubAd: '', pubMe: '', incluye: '', notas: '' };
const esVacioTmp = (e) => !e || (!String(e.nombre || '').trim() && !String(e.netoAd || '').trim() && !String(e.notas || '').trim());


const F2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n, moneda) => (n == null || n === '' ? '—' : `${moneda === 'USD' ? 'USD $' : '$'}${F2.format(Number(n))}`);
const numOrNull = (v) => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
const mismo = (a, b) => (a == null && b == null) || (a != null && b != null && Math.abs(Number(a) - Number(b)) < 0.005);

const seccionDe = (r) => Number(r.seccion) || (r.estrella ? 1 : r.es_promocion ? 3 : 2);
const grupoBase = (r) => (r.es_promocion || Number(r.seccion) === 3 ? 3 : 2);
const provKey = (r) => r.proveedor_nombre || r.proveedor_otro || r.proveedor_codigo || 'Sin proveedor';

// B55 (APP 2/05 F3): «No disponible» = activo y reservable = false en la Biblia. La vista no lo trae: se lee de productos.
export const marcarNoDisponibles = async (filas) => {
  try {
    const { data } = await supabase.from('productos').select('sku,motivo_no_reservable').eq('reservable', false);
    const m = new Map((data || []).map((p) => [p.sku, p.motivo_no_reservable || '']));
    return filas.map((r) => (m.has(r.sku) ? { ...r, reservable: false, motivo_no_reservable: m.get(r.sku) } : r));
  } catch (e) { return filas; }
};
export const noDisp = (r) => r.reservable === false;
export const ChipNoDisponible = ({ C, r }) => (noDisp(r) ? <span title={r.motivo_no_reservable || 'No disponible'} style={{ marginLeft: 6, background: `${C.muted}30`, color: C.text, border: `1px solid ${C.muted}80`, padding: '1px 6px', borderRadius: RADIO_INSIGNIA, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>NO DISPONIBLE{r.motivo_no_reservable ? <Ic n="Info" chico /> : null}</span> : null); // APP 2/34b: el motivo va en el globo (antes el texto largo se salía de la columna y se encimaba con MON)
const ordenNoDisp = (a, b) => (noDisp(a) ? 1 : 0) - (noDisp(b) ? 1 : 0); // al final de su proveedor

// ⭐ Top (orden_top) → proveedores A–Z → Promociones por proveedor (igual que Top Tours).
const agrupar = (rows) => {
  const top = rows.filter((r) => r.estrella === true || (r.estrella == null && seccionDe(r) === 1))
    .sort((a, b) => ordenNoDisp(a, b) || (a.orden_top ?? 1e9) - (b.orden_top ?? 1e9) || String(a.nombre).localeCompare(String(b.nombre), 'es'));
  const porProv = (lista) => {
    const m = {};
    lista.forEach((r) => { (m[provKey(r)] = m[provKey(r)] || []).push(r); });
    return Object.keys(m).sort((a, b) => a.localeCompare(b, 'es')).map((k) => ({
      proveedor: k, items: m[k].sort((a, b) => ordenNoDisp(a, b) || String(a.nombre).localeCompare(String(b.nombre), 'es')),
    }));
  };
  return { top, normales: porProv(rows.filter((r) => grupoBase(r) === 2)), promos: porProv(rows.filter((r) => grupoBase(r) === 3)) };
};

const coincide = (r, q) => !q || [r.nombre, r.sku, r.proveedor_nombre, r.proveedor_codigo, r.notas]
  .some((x) => String(x || '').toLowerCase().includes(q));

// ── Piezas visuales (las mismas de Top Tours) ──
const th = (C) => ({ padding: '8px 10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: C.muted, letterSpacing: '.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' });
const td = { padding: '8px 10px', verticalAlign: 'middle' };
const numTd = (C, strong) => ({ ...td, fontFamily: 'var(--au-font-text)', whiteSpace: 'nowrap', color: strong ? C.text : C.muted, fontWeight: strong ? 700 : 400 });

const MonedaBadge = ({ C, moneda }) => (
  <span style={{ background: moneda === 'USD' ? `${C.blue}18` : `${C.naranja}18`, color: moneda === 'USD' ? C.blue : C.naranja, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700 }}>{moneda || 'MXN'}</span>
);

// Componentes a nivel de módulo (si vivieran dentro del render se remontarían y cerrarían el panel del PDF).
const Grupo = ({ C, titulo, derecha, children, acento }) => (
  <div className="au-glass" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
    <div style={{ padding: '12px 16px', background: `${acento || C.rosa}10`, borderBottom: `1px solid ${acento || C.rosa}22`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{titulo}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{derecha}</div>
    </div>
    <div style={{ overflowX: 'auto' }}>{children}</div>
  </div>
);

const Buscador = ({ C, value, onChange, derecha, children }) => (
  <div className="au-glass" style={{ marginBottom: 12, padding: '12px 16px' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Buscar por nombre, SKU, proveedor o notas..."
          style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '10px 12px 10px 34px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' }} />
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4 }}>{<Ic n="Search" chico />}</span>
      </div>
      {children}
      <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{derecha}</div>
    </div>
  </div>
);

const Titulo = ({ children, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, marginTop: 28, flexWrap: 'wrap', gap: 8 }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--au-text-primary)', fontFamily: 'var(--au-font-text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 3, height: 16, background: 'var(--au-raya)', borderRadius: 2, display: 'inline-block' }} />
      {children}
    </h2>
    {right}
  </div>
);

const Ventana = ({ C, titulo, onClose, children, ancho = 520 }) => (
  <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="au-glass" style={{ background: C.card || C.surface, width: '100%', maxWidth: ancho, maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{titulo}</div>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>{<Ic n="X" chico />}</button>
      </div>
      {children}
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// Alta / edición de un producto temporal de Sala (sala_temporales: insert/update, nunca delete).
// Igual que el de Maggie + precio público (obligatorio: en Sala no hay over) e "incluye".
// ════════════════════════════════════════════════════════════════════════════
function TemporalSalaForm({ C: Cbase, modo, sku, temporalId, quien, onClose, onGuardado }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const [provs, setProvs] = useState([]);
  const [f, setF] = useState(INICIAL_TMP);
  const borradorTmp = useBorrador(claveBorrador('temporal-sala', quien), f, setF, { activo: modo !== 'editar', inicial: INICIAL_TMP, esVacio: esVacioTmp }); // Bloque 27
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cargando, setCargando] = useState(modo === 'editar');
  const [skuNuevo, setSkuNuevo] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('proveedores').select('codigo,nombre').eq('activo', true).order('nombre');
      setProvs(data || []);
      if (modo === 'editar' && temporalId != null) {
        const { data: t } = await supabase.from('sala_temporales').select('*').eq('id', temporalId).maybeSingle();
        if (t) setF({
          proveedor: t.proveedor_codigo || (t.proveedor_otro ? 'OTRO' : ''), otro: t.proveedor_otro || '', nombre: t.nombre || '',
          moneda: t.moneda || '', nacional: t.es_nacional, tipo: t.unidad_tipo || 'pax',
          cubreAd: String(t.unidad_cubre_adultos ?? 2), cubreMe: String(t.unidad_cubre_menores ?? 0),
          netoAd: t.precio_neto_adulto ?? '', netoMe: t.precio_neto_menor ?? '',
          pubAd: t.precio_publico_adulto ?? '', pubMe: t.precio_publico_menor ?? '',
          incluye: t.incluye || '', notas: t.notas || '',
        });
        setCargando(false);
      }
    })();
  }, [modo, temporalId]);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setErrs([]); };
  const esPromo = f.tipo === 'promo';
  const validar = () => {
    const e = [];
    if (!f.proveedor) e.push('Elige el proveedor.');
    if (f.proveedor === 'OTRO' && !f.otro.trim()) e.push('Escribe el nombre del proveedor ("Otro").');
    if (!f.nombre.trim()) e.push('Escribe el nombre del tour.');
    if (f.moneda !== 'MXN' && f.moneda !== 'USD') e.push('Elige la moneda.');
    if (f.nacional !== true && f.nacional !== false) e.push('Indica si es nacional.');
    if (esPromo) {
      const ad = Number(f.cubreAd), me = Number(f.cubreMe);
      if (!Number.isInteger(ad) || ad < 1) e.push('La promoción debe cubrir al menos 1 adulto.');
      if (!Number.isInteger(me) || me < 0) e.push('Los menores que cubre deben ser 0 o más.');
    }
    if (!(Number(f.netoAd) > 0)) e.push('El neto adulto debe ser mayor a 0.');
    if (!esPromo && f.netoMe !== '' && (isNaN(Number(f.netoMe)) || Number(f.netoMe) < 0)) e.push('El neto menor no es válido.');
    if (!(Number(f.pubAd) > 0)) e.push('El precio público adulto es obligatorio (en Sala no hay over).');
    else if (Number(f.netoAd) > 0 && Number(f.pubAd) < Number(f.netoAd)) e.push('El precio público adulto no puede ser menor al neto.');
    if (!esPromo && f.netoMe !== '') {
      if (!(Number(f.pubMe) > 0)) e.push('Pon el precio público menor (hay neto menor).');
      else if (Number(f.pubMe) < Number(f.netoMe)) e.push('El precio público menor no puede ser menor al neto.');
    }
    return e;
  };
  const guardar = async () => {
    const e = validar();
    if (e.length) { setErrs(e); return; }
    const sinMenor = esPromo || f.netoMe === '';
    const datos = {
      nombre: f.nombre.trim(),
      proveedor_codigo: f.proveedor === 'OTRO' ? null : f.proveedor,
      proveedor_otro: f.proveedor === 'OTRO' ? f.otro.trim() : null,
      moneda: f.moneda, es_nacional: f.nacional, unidad_tipo: esPromo ? 'promo' : 'pax',
      unidad_cubre_adultos: esPromo ? Number(f.cubreAd) : 1, unidad_cubre_menores: esPromo ? Number(f.cubreMe) : 0,
      precio_neto_adulto: Number(f.netoAd),
      precio_neto_menor: sinMenor ? null : Number(f.netoMe),
      precio_publico_adulto: Number(f.pubAd),
      precio_publico_menor: sinMenor ? null : Number(f.pubMe),
      incluye: f.incluye.trim() || null,
      notas: f.notas.trim() || null,
      actualizado_por: quien,
    };
    setBusy(true);
    if (modo === 'editar') {
      const { error } = await supabase.from('sala_temporales').update(datos).eq('id', temporalId);
      setBusy(false);
      if (error) { setErrs([`No se pudo guardar: ${error.message}`]); return; }
      await onGuardado(`Temporal ${sku} actualizado`);
      onClose();
    } else {
      const { data, error } = await supabase.from('sala_temporales').insert({ ...datos, creado_por: quien }).select('sku').single();
      setBusy(false);
      if (error) { setErrs([`No se pudo guardar: ${error.message}`]); return; }
      borradorTmp.limpiar(); // Bloque 27
      setSkuNuevo(data?.sku || '(sin SKU)');
      await onGuardado(`Temporal agregado: ${data?.sku || ''}`);
    }
  };
  const input = { width: '100%', background: C.dark, border: `1px solid ${C.border}`, borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 12px', color: C.text, fontSize: 14, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 14, color: C.muted, fontWeight: 600, marginBottom: 6, display: 'block' };
  const mon = f.moneda || 'MXN';
  const margen = (p, n) => (Number(p) > 0 && Number(n) > 0 ? Number(p) - Number(n) : null);
  const mAd = margen(f.pubAd, f.netoAd), mMe = esPromo ? null : margen(f.pubMe, f.netoMe);

  if (skuNuevo) {
    return (
      <Ventana C={C} titulo="✅ Producto temporal agregado" onClose={onClose}>
        <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7 }}>
          <div><strong>SKU asignado:</strong> <span style={{ fontFamily: 'var(--au-font-text)', color: C.yellow }}>{skuNuevo}</span></div>
          <div><strong>Tour:</strong> {f.nombre}</div>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Ya aparece en la lista de Sala (en ámbar) y en "+ Reserva" de los agentes de Sala.</div>
        <div className="au-pie">
          <button type="button" className="au-btn au-btn--primary" onClick={onClose}>Listo</button>
        </div>
      </Ventana>
    );
  }
  return (
    <Ventana C={C} titulo={modo === 'editar' ? `Editar temporal · ${sku}` : '➕ Agregar producto temporal (Sala)'} onClose={onClose}>
      {cargando ? <div style={{ color: C.muted, padding: 20 }}>Cargando…</div> : (<>
        <BandaBorrador C={C} borrador={borradorTmp} compacta />
        {errs.length > 0 && <div className="au-banner au-banner--error" style={{ marginBottom: 12, fontSize: 12 }}>{errs.map((x) => <div key={x}>{x}</div>)}</div>}
        <label style={lbl}>Proveedor</label>
        <select style={{ ...input, marginBottom: f.proveedor === 'OTRO' ? 6 : 10 }} value={f.proveedor} onChange={(e) => set({ proveedor: e.target.value })}>
          <option value="">— Elige proveedor —</option>
          {provs.map((p) => <option key={p.codigo} value={p.codigo}>{p.nombre} ({p.codigo})</option>)}
          <option value="OTRO">Otro…</option>
        </select>
        {f.proveedor === 'OTRO' && <input style={{ ...input, marginBottom: 14 }} placeholder="Nombre del proveedor" value={f.otro} onChange={(e) => set({ otro: e.target.value })} />}
        <label style={lbl}>Nombre del tour</label>
        <input style={{ ...input, marginBottom: 14 }} value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div><label style={lbl}>Moneda</label><Segmentado valor={f.moneda} opciones={[['MXN', 'MXN'], ['USD', 'USD']]} onChange={(m) => set({ moneda: m })} /></div>
          <div><label style={lbl}>Nacional</label><Segmentado valor={f.nacional} opciones={[[true, 'Sí'], [false, 'No']]} onChange={(v) => set({ nacional: v })} /></div>
          <div><label style={lbl}>Tipo</label><Segmentado valor={f.tipo} opciones={[['pax', 'Por persona'], ['promo', 'Promoción']]} onChange={(v) => set({ tipo: v })} /></div>
        </div>
        {esPromo && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}><label style={lbl}>¿Cuántos adultos cubre?</label><Stepper chico value={f.cubreAd} min={1} max={20} label="adultos que cubre" onChange={(n) => set({ cubreAd: String(n) })} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>¿Cuántos menores?</label><Stepper chico value={f.cubreMe} min={0} max={20} label="menores que cubre" onChange={(n) => set({ cubreMe: String(n) })} /></div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{esPromo ? 'Neto por promoción' : 'Neto adulto'}</label><input type="number" min="0" step="0.01" style={input} value={f.netoAd} onChange={(e) => set({ netoAd: e.target.value })} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Neto menor (vacío = no se vende a menores)</label><input type="number" min="0" step="0.01" disabled={esPromo} style={{ ...input, opacity: esPromo ? 0.4 : 1 }} value={esPromo ? '' : f.netoMe} onChange={(e) => set({ netoMe: e.target.value })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{esPromo ? 'Precio público por promoción' : 'Precio público adulto'}</label><input type="number" min="0" step="0.01" style={input} value={f.pubAd} onChange={(e) => set({ pubAd: e.target.value })} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Precio público menor</label><input type="number" min="0" step="0.01" disabled={esPromo || f.netoMe === ''} style={{ ...input, opacity: esPromo || f.netoMe === '' ? 0.4 : 1 }} value={esPromo || f.netoMe === '' ? '' : f.pubMe} onChange={(e) => set({ pubMe: e.target.value })} /></div>
        </div>
        <label style={lbl}>Incluye (un renglón por cosa)</label>
        <textarea rows={3} style={{ ...input, height: 'auto', minHeight: 72, padding: '10px 12px', resize: 'vertical', marginBottom: 14 }} value={f.incluye} onChange={(e) => set({ incluye: e.target.value })} />
        <label style={lbl}>Notas</label>
        <textarea rows={2} style={{ ...input, height: 'auto', minHeight: 72, padding: '10px 12px', resize: 'vertical', marginBottom: 14 }} value={f.notas} onChange={(e) => set({ notas: e.target.value })} />
        <div style={{ background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, borderRadius: RADIO_TARJETA, padding: '10px 12px', fontSize: 12, color: C.text }}>
          Margen: <strong>{mAd == null ? '—' : money(mAd, mon)}</strong>{esPromo ? ' por promoción' : ' adulto'}
          {!esPromo && <> · menor: <strong>{mMe == null ? '—' : money(mMe, mon)}</strong></>}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>En Sala no hay over: el precio que pongas es el que ve el agente. La comisión sigue la regla de Sala.</div>
        </div>
        <div className="au-pie">
          <button type="button" className="au-btn au-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="au-btn au-btn--primary" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : (modo === 'editar' ? 'Guardar cambios' : 'Agregar temporal')}</button>
        </div>
      </>)}
    </Ventana>
  );
}

// "📋 Pendientes de biblia" de Sala (solo Marco): temporales TMS sin ligar + ligar a la biblia + historial.
function PendientesBibliaSala({ C: Cbase, quien, onLigado }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const [temps, setTemps] = useState([]);
  const [vendidos, setVendidos] = useState({});
  const [provs, setProvs] = useState({});
  const [loading, setLoading] = useState(true);
  const [ligar, setLigar] = useState(null);
  const cargar = useCallback(async () => {
    const [t, p] = await Promise.all([
      supabase.from('sala_temporales').select('*').order('creado_en', { ascending: false }),
      supabase.from('proveedores').select('codigo,nombre'),
    ]);
    const lista = t.data || [];
    setTemps(lista);
    setProvs(Object.fromEntries((p.data || []).map((x) => [x.codigo, x.nombre])));
    const pend = lista.filter((x) => !x.sku_biblia);
    const conteos = await Promise.all(pend.map((x) => supabase.from('reservas').select('id', { count: 'exact', head: true })
      .contains('servicios_detalle', JSON.stringify([{ sku: x.sku }])).eq('es_vigente', true).neq('estado', 'eliminado')
      .then((r) => [x.sku, r.error ? null : r.count])));
    setVendidos(Object.fromEntries(conteos));
    setLoading(false);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const abrirLigar = async (temp) => {
    setLigar({ temp, productos: [], q: '', elegido: null, err: '', busy: false, cargando: true });
    const { data } = await supabase.from('productos').select('sku,nombre,proveedor_codigo,moneda').eq('activo', true).order('nombre');
    setLigar((l) => l && ({ ...l, productos: data || [], cargando: false }));
  };
  const confirmarLigar = async () => {
    if (!ligar?.elegido) return;
    setLigar((l) => ({ ...l, busy: true, err: '' }));
    const { data, error } = await supabase.rpc('sala_ligar_temporal', { p_temporal_id: ligar.temp.id, p_sku_biblia: ligar.elegido.sku, p_por: quien });
    if (error || !data?.ok) { setLigar((l) => ({ ...l, busy: false, err: error?.message || data?.error || 'No se pudo ligar' })); return; }
    const msg = `${ligar.temp.sku} ligado a ${ligar.elegido.sku}`;
    setLigar(null);
    await cargar();
    await onLigado(msg);
  };

  const pendientes = temps.filter((x) => !x.sku_biblia);
  const ligados = temps.filter((x) => x.sku_biblia);
  const provDe = (x) => (x.proveedor_codigo ? (provs[x.proveedor_codigo] || x.proveedor_codigo) : (x.proveedor_otro ? `${x.proveedor_otro} (otro)` : '—'));
  const fecha = (v) => (v ? new Date(v).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
  const thS = th(C);
  const tdS = { ...td, fontSize: 12, color: C.text };
  const ql = (ligar?.q || '').trim().toLowerCase();
  const opciones = ligar ? ligar.productos.filter((p) => !ql || p.sku.toLowerCase().includes(ql) || String(p.nombre).toLowerCase().includes(ql)).slice(0, 25) : [];

  if (loading) return <div style={{ padding: 30, textAlign: 'center', color: C.muted }}>Cargando pendientes de biblia…</div>;
  return (
    <>
      <Titulo>{<Ic n="ClipboardList" chico />} Pendientes de biblia · Sala ({pendientes.length})</Titulo>
      <div className="au-glass" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {['SKU', 'Nombre', 'Proveedor', 'Mon', 'Nal', 'Tipo / cubre', 'Neto Ad', 'Neto Men', 'Público Ad', 'Público Men', 'Notas', 'Creado por', 'Fecha', 'Vendido', ''].map((h) => <th key={h} style={thS}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pendientes.length === 0 ? (
                <tr><td colSpan={15} style={{ padding: 30, textAlign: 'center', color: C.muted }}>Sin productos temporales pendientes.</td></tr>
              ) : pendientes.map((x) => (
                <tr key={x.id} style={{ borderBottom: `1px solid ${C.border}`, background: `${C.yellow}10`, opacity: x.activo === false ? 0.55 : 1 }}>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', fontWeight: 700, color: C.yellow, whiteSpace: 'nowrap' }}>{x.sku}</td>
                  <td style={tdS}>{x.nombre}{x.activo === false && <span className="au-badge au-badge--neutral" style={{ fontSize: 12, marginLeft: 6 }}>apagado</span>}</td>
                  <td style={{ ...tdS, color: C.muted }}>{provDe(x)}</td>
                  <td style={tdS}><MonedaBadge C={C} moneda={x.moneda} /></td>
                  <td style={tdS}>{x.es_nacional ? 'Sí' : 'No'}</td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{x.unidad_tipo === 'promo' ? `Promo ${x.unidad_cubre_adultos}A/${x.unidad_cubre_menores}M` : 'Por persona'}</td>
                  <td style={numTd(C)}>{money(x.precio_neto_adulto, x.moneda)}</td>
                  <td style={numTd(C)}>{money(x.precio_neto_menor, x.moneda)}</td>
                  <td style={numTd(C, true)}>{money(x.precio_publico_adulto, x.moneda)}</td>
                  <td style={numTd(C, true)}>{money(x.precio_publico_menor, x.moneda)}</td>
                  <td style={{ ...tdS, fontSize: 12, color: C.muted, maxWidth: 180 }}>{x.notas || '—'}</td>
                  <td style={{ ...tdS, color: C.muted }}>{x.creado_por || '—'}</td>
                  <td style={{ ...tdS, color: C.muted, whiteSpace: 'nowrap' }}>{fecha(x.creado_en)}</td>
                  <td style={tdS}>{vendidos[x.sku] == null ? '—' : vendidos[x.sku]}</td>
                  <td style={tdS}><button type="button" className="au-btn au-btn--secondary" onClick={() => abrirLigar(x)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12, whiteSpace: 'nowrap' }}>Ligar a SKU</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Titulo>Historial de temporales ligados (Sala)</Titulo>
      <div className="au-glass" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${C.border}` }}>{['Temporal', 'Nombre', '→ SKU biblia', 'Ligado por', 'Fecha'].map((h) => <th key={h} style={thS}>{h}</th>)}</tr></thead>
            <tbody>
              {ligados.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: C.muted }}>Todavía no se ha ligado ningún temporal de Sala.</td></tr>
              ) : ligados.map((x) => (
                <tr key={x.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', color: C.muted }}>{x.sku}</td>
                  <td style={tdS}>{x.nombre}</td>
                  <td style={{ ...tdS, fontFamily: 'var(--au-font-text)', fontWeight: 700, color: C.green }}>{x.sku_biblia}</td>
                  <td style={{ ...tdS, color: C.muted }}>{x.ligado_por || '—'}</td>
                  <td style={{ ...tdS, color: C.muted, whiteSpace: 'nowrap' }}>{fecha(x.ligado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {ligar && (
        <Ventana C={C} titulo={`Ligar ${ligar.temp.sku} a la biblia`} onClose={() => !ligar.busy && setLigar(null)}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>{ligar.temp.nombre} · {provDe(ligar.temp)}</div>
          {ligar.err && <div className="au-banner au-banner--error" style={{ marginBottom: 14, fontSize: 12 }}>{ligar.err}</div>}
          {ligar.cargando ? <div style={{ color: C.muted }}>Cargando biblia…</div> : (<>
            <input autoFocus value={ligar.q} onChange={(e) => setLigar((l) => ({ ...l, q: e.target.value, elegido: null }))} placeholder="Buscar SKU o nombre en la biblia…"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '9px 12px', color: C.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
            <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: RADIO_TARJETA }}>
              {opciones.length === 0 ? <div style={{ padding: 14, color: C.muted, fontSize: 12 }}>Sin coincidencias.</div> : opciones.map((p) => (
                <div key={p.sku} onClick={() => setLigar((l) => ({ ...l, elegido: p }))}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: ligar.elegido?.sku === p.sku ? `${C.green}18` : 'transparent', fontSize: 12, color: C.text }}>
                  <strong style={{ fontFamily: 'var(--au-font-text)' }}>{p.sku}</strong> · {p.nombre} <span style={{ color: C.muted }}>({p.proveedor_codigo} · {p.moneda})</span>
                </div>
              ))}
            </div>
            {ligar.elegido && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.text, background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: RADIO_TARJETA, padding: '8px 12px' }}>
                Se copiarán {<Ic n="Star" chico />}, orden, precios e "incluye" de {ligar.temp.sku} a <strong>{ligar.elegido.sku}</strong> en la máscara de Sala, y el temporal dejará de salir en la lista.
              </div>
            )}
          </>)}
          <div className="au-pie">
            <button type="button" className="au-btn au-btn--ghost" onClick={() => setLigar(null)} disabled={ligar.busy}>Cancelar</button>
            <button type="button" className="au-btn au-btn--primary" onClick={confirmarLigar} disabled={!ligar.elegido || ligar.busy}>{ligar.busy ? 'Ligando…' : 'Confirmar y ligar'}</button>
          </div>
        </Ventana>
      )}
    </>
  );
}

// PDF de la LISTA de Sala (la de siempre, con netos; solo lo prendido). Precio = el de sala_lista (editado o biblia).
const descargarListaSala = async (filas) => {
  try {
    if (!window.jspdf) {
      const cargar = (src) => new Promise((resolve, reject) => { const s = document.createElement('script'); s.src = src; s.onload = resolve; s.onerror = reject; document.head.appendChild(s); });
      await cargar('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      await cargar('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');
    }
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const porProv = {};
    filas.forEach((r) => { (porProv[provKey(r)] = porProv[provKey(r)] || []).push(r); });
    const claves = Object.keys(porProv).sort((a, b) => a.localeCompare(b, 'es'));
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(247, 71, 128);
    doc.text('CANCUN100 — LISTA DE PRECIOS', 14, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text(`${new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })} · ${filas.length} productos · ${claves.length} proveedores · cancun100.com by Xpectative Travel`, 14, 21);
    doc.setDrawColor(247, 71, 128); doc.setLineWidth(0.4); doc.line(14, 24, 196, 24);
    let y = 30;
    const pm = (n, mon) => (n != null ? `${mon === 'USD' ? 'USD $' : '$'}${F2.format(Number(n))}` : '-');
    claves.forEach((k) => {
      const items = porProv[k].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es'));
      if (y > 260) { doc.addPage(); y = 15; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(247, 71, 128);
      doc.text(`${k} — ${items.length} producto${items.length !== 1 ? 's' : ''}`, 14, y);
      doc.autoTable({
        startY: y + 3,
        head: [['SKU', 'PRODUCTO', 'MON', 'PUB AD', 'PUB ME', 'NETO AD', 'NETO ME', 'NAL', 'NOTAS']],
        body: items.map((r) => [r.sku || '', `${r.estrella ? '* ' : ''}${r.nombre || ''}${r.es_temporal ? ' (temporal)' : ''}`, r.moneda || 'MXN',
          pm(r.precio_adulto, r.moneda), pm(r.precio_menor, r.moneda), pm(r.precio_neto_adulto, r.moneda), pm(r.precio_neto_menor, r.moneda),
          r.es_nacional ? 'NAL' : 'EXT', r.notas || '']),
        styles: { fontSize: 12, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [247, 71, 128], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 12 },
        columnStyles: { 0: { cellWidth: 17, fontStyle: 'bold' }, 1: { cellWidth: 55 }, 2: { cellWidth: 10, halign: 'center' }, 3: { cellWidth: 17, halign: 'right' }, 4: { cellWidth: 17, halign: 'right' }, 5: { cellWidth: 17, halign: 'right' }, 6: { cellWidth: 17, halign: 'right' }, 7: { cellWidth: 10, halign: 'center' }, 8: { cellWidth: 22 } },
        theme: 'grid',
      });
      y = doc.lastAutoTable.finalY + 6;
    });
    const n = doc.internal.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
      doc.setPage(i); doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(140, 140, 140);
      doc.text(lineaContacto('sala'), 14, 288);
      doc.text('cancun100.com by Xpectative Travel · Precios sujetos a cambio sin previo aviso', 14, 292);
      doc.text(`Pagina ${i} de ${n}`, 180, 292);
    }
    doc.save(`Lista_Precios_Cancun100_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (e) {
    console.error('Error generando PDF de lista:', e && e.message);
    alert('No se pudo generar el PDF. Intenta de nuevo.');
  }
};

// ════════════════════════════════════════════════════════════════════════════
// SALA DE VENTAS — editor (admin) / lista (agente)
// ════════════════════════════════════════════════════════════════════════════
export default function SalaPreciosTab({ C: Cbase, loggedAgent = null, modo = 'admin', editable = false, esSuperAdmin = false, opcionesPrecioPDF = ['sala'], netosPorSku = null }) {
  const C = Cbase; // APP 2/20 Fase A: C.red viene del tema
  const esAgente = modo === 'agente';
  const puedeEditar = !esAgente && editable;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [mostrarApagados, setMostrarApagados] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [globoT, setGloboT] = useState(null);
  const [formTmp, setFormTmp] = useState(null);
  const [vista, setVista] = useState('lista');
  const [nPendientes, setNPendientes] = useState(null);
  const inputsAd = useRef({});
  const movil = useMovil(); // APP 2/28: renglón compacto en el celular
  const [globoN, setGloboN] = useState(null); // APP 2/28: { r, pos } del globo ⓘ de la nota de venta
  const [abiertoM, setAbiertoM] = useState(null); // APP 2/28: sku desplegado en el celular
  const abrirNota = (r, pos) => setGloboN({ r, pos });
  const cerrarNota = useCallback(() => setGloboN(null), []);
  const quien = loggedAgent?.nombre || 'admin';
  const showToast = (tipo, msg) => { setToast({ tipo, msg }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.from('sala_lista').select('*');
    if (e) setError(e.message || 'No se pudo cargar la lista de Sala');
    else { setError(''); setRows(await marcarNoDisponibles(data || [])); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!globoT) return undefined;
    const cerrar = () => setGloboT(null);
    window.addEventListener('scroll', cerrar, true);
    return () => window.removeEventListener('scroll', cerrar, true);
  }, [globoT]);
  const contarPendientes = useCallback(async () => {
    if (!esSuperAdmin || esAgente) return;
    const { count, error: e } = await supabase.from('sala_temporales').select('id', { count: 'exact', head: true }).is('sku_biblia', null);
    setNPendientes(e ? null : count);
  }, [esSuperAdmin, esAgente]);
  useEffect(() => { contarPendientes(); }, [contarPendientes]);

  // Neto de la fila: sala_lista ya lo trae (biblia o temporal); respaldo: la biblia que pasa App.
  const netoAd = (r) => r.precio_neto_adulto ?? netosPorSku?.[r.sku]?.ad ?? null;
  const netoMe = (r) => r.precio_neto_menor ?? netosPorSku?.[r.sku]?.men ?? null;
  // Sugerido = precio de la biblia (en un temporal no hay biblia: su propio precio público).
  const sugAd = (r) => (r.es_temporal ? r.precio_adulto : (r.precio_biblia_adulto ?? r.precio_adulto));
  const sugMe = (r) => (r.es_temporal ? r.precio_menor : (r.precio_biblia_menor ?? r.precio_menor));

  // Guarda cambios (nunca delete). Biblia → upsert por sku en sala_precios; temporal → update por temporal_id.
  const guardar = async (filas, okMsg) => {
    setBusy(true);
    const lista = (Array.isArray(filas) ? filas : [filas]).map((f) => ({ ...f, actualizado_por: quien }));
    const porSku = new Map(lista.map((x) => [x.sku, x]));
    const aplicar = () => setRows((prev) => prev.map((r) => {
      const c = porSku.get(r.sku);
      if (!c) return r;
      const n = { ...r };
      if ('estrella' in c) { n.estrella = c.estrella; n.seccion = c.estrella ? 1 : grupoBase(r); }
      if ('orden_top' in c) n.orden_top = c.orden_top;
      if ('activo' in c) n.activo_sala = c.activo;
      if ('precio_publico_adulto' in c) n.precio_adulto = c.precio_publico_adulto ?? r.precio_biblia_adulto;
      if ('precio_publico_menor' in c) n.precio_menor = c.precio_publico_menor ?? r.precio_biblia_menor;
      if (!r.es_temporal && ('precio_publico_adulto' in c || 'precio_publico_menor' in c)) {
        n.precio_editado = !(mismo(n.precio_adulto, r.precio_biblia_adulto) && (r.precio_biblia_menor == null || mismo(n.precio_menor, r.precio_biblia_menor)));
      }
      return n;
    }));
    aplicar();
    const idTmp = new Map(rows.filter((r) => r.es_temporal && r.temporal_id != null).map((r) => [r.sku, r.temporal_id]));
    const deBiblia = lista.filter((f) => !idTmp.has(f.sku));
    const temporales = lista.filter((f) => idTmp.has(f.sku));
    let e = null;
    if (deBiblia.length) ({ error: e } = await supabase.from('sala_precios').upsert(deBiblia, { onConflict: 'sku' }));
    if (!e && temporales.length) {
      const res = await Promise.all(temporales.map(({ sku, ...cambios }) => supabase.from('sala_temporales').update(cambios).eq('id', idTmp.get(sku))));
      e = res.find((x) => x.error)?.error || null;
    }
    setBusy(false);
    if (e) { showToast('error', `No se pudo guardar: ${e.message}`); await load(); return false; }
    showToast('ok', okMsg || 'Guardado');
    await load();
    aplicar();
    return true;
  };

  const maxOrden = () => rows.reduce((m, r) => (r.estrella && r.orden_top != null ? Math.max(m, Number(r.orden_top)) : m), 0);
  const toggleEstrella = (r) => (r.estrella
    ? guardar({ sku: r.sku, estrella: false, orden_top: null }, `⭐ quitada: ${r.nombre}`)
    : guardar({ sku: r.sku, estrella: true, orden_top: maxOrden() + 1 }, `⭐ agregada: ${r.nombre}`));
  const toggleActivo = (r) => guardar({ sku: r.sku, activo: !r.activo_sala }, `${r.activo_sala ? 'Apagado' : 'Prendido'}: ${r.nombre}`);
  const mover = (lista, i, dir) => {
    const a = lista[i], b = lista[i + dir];
    if (!a || !b) return;
    const ordenes = lista.map((r) => r.orden_top);
    const limpios = ordenes.every((o) => o != null) && new Set(ordenes).size === ordenes.length;
    if (limpios) guardar([{ sku: a.sku, orden_top: b.orden_top }, { sku: b.sku, orden_top: a.orden_top }], 'Orden actualizado');
    else {
      const nueva = [...lista];
      [nueva[i], nueva[i + dir]] = [nueva[i + dir], nueva[i]];
      guardar(nueva.map((r, k) => ({ sku: r.sku, orden_top: k + 1 })), 'Orden actualizado');
    }
  };
  const proveedorMasivo = (nombre, activo) => {
    const skus = rows.filter((r) => provKey(r) === nombre).map((r) => r.sku);
    if (!skus.length) return;
    if (!window.confirm(`¿${activo ? 'PRENDER' : 'APAGAR'} los ${skus.length} productos de ${nombre} en la lista de Sala?\n(Incluye sus promociones y los que tengan ⭐.)`)) return;
    guardar(skus.map((sku) => ({ sku, activo })), `${nombre}: ${skus.length} producto(s) ${activo ? 'prendidos' : 'apagados'}`);
  };

  // ── Edición de precios (sugerido = biblia; nunca menor al neto) ──
  const draftDe = (r) => drafts[r.sku] || { ad: r.precio_adulto ?? '', men: r.precio_menor ?? '' };
  const setDraft = (r, campo, v) => setDrafts((d) => ({ ...d, [r.sku]: { ...draftDe(r), [campo]: v } }));
  const menorAplica = (r) => sugMe(r) != null || r.precio_menor != null;
  const erroresDe = (r, d) => {
    const errs = [];
    const ad = numOrNull(d.ad), men = numOrNull(d.men);
    if (d.ad !== '' && ad == null) errs.push('Precio adulto no es un número');
    if (r.es_temporal && d.ad === '') errs.push('Un temporal necesita precio adulto');
    if (d.men !== '' && d.men != null && men == null) errs.push('Precio menor no es un número');
    if (ad != null && netoAd(r) != null && ad < Number(netoAd(r))) errs.push(`Adulto menor al neto (${money(netoAd(r), r.moneda)})`);
    if (men != null && netoMe(r) != null && men < Number(netoMe(r))) errs.push(`Menor menor al neto (${money(netoMe(r), r.moneda)})`);
    return errs;
  };
  const cambiado = (r, d) => !mismo(numOrNull(d.ad), r.precio_adulto) || (menorAplica(r) && !mismo(numOrNull(d.men), r.precio_menor));
  const guardarPrecio = async (r) => {
    const d = draftDe(r);
    if (erroresDe(r, d).length) return;
    // Producto de biblia: vacío o igual a la biblia = sin edición (null → hereda la biblia).
    // Temporal: guarda el número tal cual (no hay biblia de dónde heredar).
    const norm = (v, sug) => { const n = numOrNull(v); return r.es_temporal ? n : (n == null || mismo(n, sug) ? null : n); };
    const cambios = { sku: r.sku, precio_publico_adulto: norm(d.ad, r.precio_biblia_adulto) };
    if (menorAplica(r)) cambios.precio_publico_menor = norm(d.men, r.precio_biblia_menor);
    if (await guardar(cambios, `Precio guardado: ${r.nombre}`)) setDrafts((x) => { const n = { ...x }; delete n[r.sku]; return n; });
  };
  const volverBiblia = async (r) => {
    if (await guardar({ sku: r.sku, precio_publico_adulto: null, precio_publico_menor: null }, `↺ Precio de la biblia: ${r.nombre}`)) {
      setDrafts((x) => { const n = { ...x }; delete n[r.sku]; return n; });
    }
  };

  // ── Filtrado ──
  const ql = q.trim().toLowerCase();
  const base = esAgente ? rows.filter((r) => r.activo_sala) : rows;
  const visibles = base.filter((r) => coincide(r, ql) && (esAgente || mostrarApagados || r.activo_sala));
  const { top, normales, promos } = agrupar(visibles);
  const total = base.length;
  const prendidos = base.filter((r) => r.activo_sala).length;
  const estrellas = base.filter((r) => r.estrella && (!esAgente || r.activo_sala)).length;

  const celdaNAL = (r) => (
    <td style={tdP}>
      <span style={{ background: r.es_nacional ? `${C.yellow}20` : `${C.muted}20`, color: r.es_nacional ? C.yellow : C.muted, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700 }}>{r.es_nacional ? 'NAL' : 'EXT'}</span>
    </td>
  );
  const opcionesPDF = esAgente ? ['sala'] : (opcionesPrecioPDF || ['sala']);
  const celdaPDF = (r) => (
    <td style={{ ...tdP, whiteSpace: 'nowrap' }}>
      <DescriptivoBoton C={C} loggedAgent={loggedAgent} esAgente={esAgente} opcionesPrecio={opcionesPDF}
        producto={{ sku: r.sku, nombre: r.nombre, moneda: r.moneda, proveedor_codigo: r.proveedor_codigo, es_nacional: r.es_nacional,
          es_temporal: !!r.es_temporal, incluye: r.incluye, notas: r.notas }} />
    </td>
  );
  const marcaTemporal = (
    <span title="Producto temporal: todavía no está en la biblia" style={{ marginLeft: 6, background: `${C.yellow}25`, color: C.yellow, border: `1px solid ${C.yellow}55`, padding: '1px 6px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700, whiteSpace: 'nowrap' }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</span>
  );
  const inputSt = (err) => ({ width: 78, // APP 2/28: cabe en la columna pareja junto al globo de alerta
    height: 32, boxSizing: 'border-box', background: C.dark, border: `1px solid ${err ? C.red : C.border}`, borderRadius: RADIO, padding: '0 8px', color: C.text, fontSize: 12, fontFamily: 'var(--au-font-text)', outline: 'none', boxSizing: 'border-box' });


  const filaAgente = (r) => (
    <tr key={r.sku} style={{ borderBottom: `1px solid ${C.border}`, height: ALTO_FILA, background: r.es_temporal ? `${C.yellow}14` : 'transparent', opacity: noDisp(r) ? 0.55 : 1, filter: noDisp(r) ? 'grayscale(1)' : 'none' }}>
      <td style={{ ...tdP, fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}><span style={linea1}>{r.sku}</span></td>
      <td style={{ ...tdP, color: C.text, fontWeight: 500 }} title={r.nombre}><div style={clamp2}>{r.estrella && <span title="Top" style={{ marginRight: 4 }}>{<Ic n="Star" chico />}</span>}{r.nombre}{r.es_temporal && marcaTemporal}<ChipNoDisponible C={C} r={r} /></div></td>
      <td style={tdP}><MonedaBadge C={C} moneda={r.moneda} /></td>
      <td style={numP(C, true)}>{money(r.precio_adulto, r.moneda)}</td>
      <td style={numP(C)}>{r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)}</td>
      <td style={numP(C, true)}>{money(netoAd(r), r.moneda)}</td>
      <td style={numP(C)}>{money(netoMe(r), r.moneda)}</td>
      {celdaNAL(r)}
      <CeldaNota C={C} r={r} movil={movil} onAbrir={abrirNota} />
      {celdaPDF(r)}
    </tr>
  );

  // Globo ámbar del temporal (editor: creado por, fecha, SKU TMS y "Editar datos").
  const globoTemporal = (r) => {
    const abierto = globoT?.sku === r.sku;
    const abrir = (e) => {
      if (abierto) { setGloboT(null); return; }
      const b = e.currentTarget.getBoundingClientRect();
      setGloboT({ sku: r.sku, top: Math.min(b.bottom + 6, window.innerHeight - 200), left: Math.max(8, Math.min(b.left - 10, window.innerWidth - 300 - 8)) });
    };
    const fecha = r.temporal_creado_en ? new Date(r.temporal_creado_en).toLocaleString('es-MX', { timeZone: 'America/Cancun', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
    return (
      <span style={{ display: 'inline-block', marginLeft: 6, verticalAlign: 'middle' }}>
        <button type="button" onClick={abrir} title="Producto temporal: todavía no está en la biblia"
          style={{ background: `${C.yellow}25`, color: C.yellow, border: `1px solid ${C.yellow}55`, padding: '1px 7px', borderRadius: RADIO_INSIGNIA, fontSize:12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</button>
        {abierto && (
          <div style={{ position: 'fixed', zIndex: 300, top: globoT.top, left: globoT.left, width: 300, background: C.surface, border: `1px solid ${C.yellow}`, borderRadius: RADIO_TARJETA, padding: 14, boxShadow:'var(--au-shadow-card)', fontSize: 12, color: C.text, whiteSpace: 'normal', textAlign: 'left', fontWeight: 400 }}>
            <div style={{ fontWeight: 700, color: C.yellow, marginBottom: 8 }}>{<Ic n="Hourglass" chico />} Temporal — falta en biblia</div>
            <div><strong>SKU:</strong> {r.sku}</div>
            <div><strong>Creado por:</strong> {r.temporal_creado_por || '—'}</div>
            <div><strong>Fecha:</strong> {fecha}</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Marco lo liga a la biblia desde "Pendientes de biblia".</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {puedeEditar && r.temporal_id != null && <button type="button" className="au-btn au-btn--secondary" onClick={() => { setGloboT(null); setFormTmp({ modo: 'editar', sku: r.sku, temporalId: r.temporal_id }); }} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Editar datos</button>}
              <button type="button" className="au-btn au-btn--ghost" onClick={() => setGloboT(null)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Cerrar</button>
            </div>
          </div>
        )}
      </span>
    );
  };


  const filaAdmin = (r, ctx) => {
    const d = draftDe(r);
    const errs = puedeEditar ? erroresDe(r, d) : [];
    const sucio = puedeEditar && cambiado(r, d);
    const menorDeshab = !menorAplica(r);
    const btn = (etq, titulo, onClick, primario, deshab) => <button type="button" className={`au-btn ${primario ? 'au-btn--primary' : 'au-btn--secondary'}`} disabled={busy || deshab} title={titulo} onClick={onClick} style={{ height: 26, boxSizing: 'border-box', padding: '0 7px', fontSize: 12, lineHeight: '24px' }}>{etq}</button>;
    return (
      <tr key={r.sku} style={{ borderBottom: `1px solid ${C.border}`, height: ALTO_FILA, opacity: r.activo_sala ? 1 : 0.5, background: r.es_temporal ? `${C.yellow}14` : 'transparent' }}>
        <td style={{ ...tdP, whiteSpace: 'nowrap' }}>
          {puedeEditar ? (
            <button type="button" disabled={busy} onClick={() => toggleEstrella(r)} title={r.estrella ? 'Quitar de Top' : 'Poner en Top'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, filter: r.estrella ? 'none' : 'grayscale(1)', opacity: r.estrella ? 1 : 0.35, padding: 0 }}>{<Ic n="Star" chico />}</button>
          ) : (r.estrella ? '⭐' : '')}
          {ctx && puedeEditar && (
            <span style={{ marginLeft: 4, display: 'inline-flex', gap: 1 }}>
              <button type="button" className="au-btn au-btn--ghost" disabled={busy || ctx.i === 0} onClick={() => mover(ctx.lista, ctx.i, -1)} style={{ padding: '2px 4px', fontSize: 12 }}>↑</button>
              <button type="button" className="au-btn au-btn--ghost" disabled={busy || ctx.i === ctx.lista.length - 1} onClick={() => mover(ctx.lista, ctx.i, 1)} style={{ padding: '2px 4px', fontSize: 12 }}>↓</button>
            </span>
          )}
        </td>
        <td style={tdP}>
          {puedeEditar ? (
            <button type="button" disabled={busy} onClick={() => toggleActivo(r)} className="au-switch" data-on={!!r.activo_sala} style={{ display: 'inline-block' }}>
              <span className="au-switch__knob" />
            </button>
          ) : (
            <span className={`au-badge ${r.activo_sala ? 'au-badge--success' : 'au-badge--neutral'}`} style={{ fontSize: 12 }}>{r.activo_sala ? 'Sí' : 'No'}</span>
          )}
        </td>
        <td style={{ ...tdP, fontSize: 12, color: C.muted, fontWeight: 700, whiteSpace: 'nowrap' }}><span style={linea1}>{r.sku}</span></td>
        <td style={{ ...tdP, color: C.text, fontWeight: 500, opacity: noDisp(r) ? 0.6 : 1 }} title={errs.length ? errs.join(' · ') : r.nombre}>
          <div style={clamp2}>
            {errs.length > 0 && <span title={errs.join(' · ')} style={{ color: C.red, fontWeight: 800, marginRight: 4 }}>!</span>}
            {r.nombre}
            {r.es_temporal && globoTemporal(r)}
            <ChipNoDisponible C={C} r={r} />
            {r.precio_editado && !r.es_temporal && <span className="au-badge au-badge--yellow" style={{ fontSize: 12, marginLeft: 6 }}>editado</span>}
          </div>
        </td>
        <td style={tdP}><MonedaBadge C={C} moneda={r.moneda} /></td>
        <td style={{ ...tdP, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {puedeEditar
            ? <input type="number" step="0.01" min="0" value={d.ad} ref={(el) => { inputsAd.current[r.sku] = el; }} onChange={(e) => setDraft(r, 'ad', e.target.value)} style={inputSt(errs.some((x) => x.startsWith('Adulto') || x.includes('adulto')))} />
            : <span style={{ fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{money(r.precio_adulto, r.moneda)}</span>}
        </td>
        <td style={{ ...tdP, whiteSpace: 'nowrap', textAlign: 'right' }}>
          {puedeEditar
            ? <input type="number" step="0.01" min="0" disabled={menorDeshab} value={menorDeshab ? '' : d.men} placeholder={menorDeshab ? 'n/a' : ''}
                onChange={(e) => setDraft(r, 'men', e.target.value)} style={{ ...inputSt(errs.some((x) => x.startsWith('Menor') || x.includes('menor no'))), opacity: menorDeshab ? 0.4 : 1 }} />
            : <span style={{ color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)}</span>}
        </td>
        <td style={numP(C, true)}>{money(netoAd(r), r.moneda)}</td>
        <td style={numP(C)}>{money(netoMe(r), r.moneda)}</td>
        <td style={{ ...tdP, fontSize: 12, color: C.muted, whiteSpace: 'nowrap', textAlign: 'right' }} title="Precio de la biblia (adulto / menor)">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', maxWidth: '100%' }}>
            <span style={{ ...linea1, fontVariantNumeric: 'tabular-nums' }}>{r.es_temporal ? '—' : <>{money(sugAd(r), r.moneda)}{sugMe(r) != null && <> / {money(sugMe(r), r.moneda)}</>}</>}</span>
            {puedeEditar && sucio && btn('✓', 'Guardar precio', () => guardarPrecio(r), true, errs.length > 0)}
            {puedeEditar && (r.precio_editado && !r.es_temporal) && btn('↺', 'Volver al precio de la biblia', () => volverBiblia(r), false)}
          </span>
        </td>
        {celdaNAL(r)}
        <CeldaNota C={C} r={r} movil={movil} onAbrir={abrirNota} />
        {celdaPDF(r)}
      </tr>
    );
  };


  // APP 2/28 (RP-10): tabla pareja (mismas columnas, orden y ancho; renglones del mismo alto) y renglón compacto en el celular.
  const listaMovil = (items) => (
    <div>
      {items.map((r) => {
        const d = draftDe(r); const errs = puedeEditar ? erroresDe(r, d) : []; const sucio = puedeEditar && cambiado(r, d);
        return <FilaMovil key={r.sku} C={C} r={r} money={money} pubAd={r.precio_adulto} netoAd={netoAd(r)} abierto={abiertoM === r.sku} onToggle={() => setAbiertoM((v) => (v === r.sku ? null : r.sku))} onAbrirNota={abrirNota}
          apagado={!esAgente && !r.activo_sala} cabecera={r.estrella ? <span title="Top" style={{ marginRight: 4 }}>{<Ic n="Star" chico />}</span> : null}
          detalle={<>
            <DatoMovil C={C} l="Pub ME" v={r.precio_menor == null ? '—' : money(r.precio_menor, r.moneda)} />
            <DatoMovil C={C} l="Neto ME" v={money(netoMe(r), r.moneda)} />
            <MonedaBadge C={C} moneda={r.moneda} />
            <span style={{ background: r.es_nacional ? `${C.yellow}20` : `${C.muted}20`, color: r.es_nacional ? C.yellow : C.muted, padding: '2px 6px', borderRadius: RADIO_INSIGNIA, fontSize: 12, fontWeight: 700 }}>{r.es_nacional ? 'NAL' : 'EXT'}</span>
            <span style={{ fontSize: 12, color: C.muted }}>{r.sku}</span>
            {r.es_temporal && (esAgente ? marcaTemporal : globoTemporal(r))}<ChipNoDisponible C={C} r={r} />
            <table style={{ borderCollapse: 'collapse' }}><tbody><tr>{celdaPDF(r)}</tr></tbody></table>
            {!esAgente && (
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                {puedeEditar && <button type="button" disabled={busy} onClick={() => toggleActivo(r)} className="au-switch" data-on={!!r.activo_sala} style={{ display: 'inline-block' }}><span className="au-switch__knob" /></button>}
                {puedeEditar && <button type="button" disabled={busy} onClick={() => toggleEstrella(r)} title={r.estrella ? 'Quitar de Top' : 'Poner en Top'} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, filter: r.estrella ? 'none' : 'grayscale(1)', opacity: r.estrella ? 1 : 0.35, padding: 0 }}>{<Ic n="Star" chico />}</button>}
                {puedeEditar && <input type="number" step="0.01" min="0" value={d.ad} onChange={(e) => setDraft(r, 'ad', e.target.value)} aria-label="Precio adulto" style={inputSt(errs.length > 0)} />}
                {puedeEditar && <input type="number" step="0.01" min="0" disabled={!menorAplica(r)} value={!menorAplica(r) ? '' : d.men} onChange={(e) => setDraft(r, 'men', e.target.value)} aria-label="Precio menor" style={{ ...inputSt(false), opacity: !menorAplica(r) ? 0.4 : 1 }} />}
                {puedeEditar && sucio && <button type="button" className="au-btn au-btn--primary" disabled={busy || errs.length > 0} onClick={() => guardarPrecio(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Guardar</button>}
                {puedeEditar && (r.precio_editado && !r.es_temporal) && <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => volverBiblia(r)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>↺</button>}
                {errs.length > 0 && <span style={{ color: C.red, fontSize: 12, fontWeight: 600, width: '100%' }}>{errs.join(' · ')}</span>}
              </span>
            )}
          </>} />;
      })}
    </div>
  );
  const tabla = (items, conFlechas) => (movil ? listaMovil(items) : (
    <TablaPareja C={C} cols={esAgente ? COLS_AGENTE : COLS_ADMIN}>
      {items.map((r, i) => (esAgente ? filaAgente(r) : filaAdmin(r, conFlechas ? { lista: items, i } : null)))}
    </TablaPareja>
  ));

  const grupoProveedor = (g, prefijo) => {
    const enGrupo = esAgente ? g.items.length : rows.filter((r) => provKey(r) === g.proveedor && grupoBase(r) === grupoBase(g.items[0])).length;
    const prendGrupo = rows.filter((r) => provKey(r) === g.proveedor && grupoBase(r) === grupoBase(g.items[0]) && r.activo_sala).length;
    return (
      <Grupo key={`${prefijo}-${g.proveedor}`} C={C} titulo={g.proveedor}
        derecha={<>
          <span style={{ fontSize: 12, color: C.muted }}>{esAgente ? `${enGrupo} producto${enGrupo !== 1 ? 's' : ''}` : `${prendGrupo}/${enGrupo} prendidos`}</span>
          {puedeEditar && <>
            <button type="button" className="au-btn au-btn--ghost" disabled={busy} onClick={() => proveedorMasivo(g.proveedor, false)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Apagar proveedor</button>
            <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => proveedorMasivo(g.proveedor, true)} style={{ height: 32, boxSizing: 'border-box', padding: '0 10px', fontSize: 12 }}>Prender proveedor</button>
          </>}
        </>}>
        {tabla(g.items, false)}
      </Grupo>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando lista de Sala…</div>;

  return (
    <>
      {toast && <div className={`au-banner ${toast.tipo === 'error' ? 'au-banner--error' : 'au-banner--success'}`} style={{ position: 'fixed', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 400, fontWeight: 600, boxShadow:'var(--au-shadow-card)' }}>{toast.msg}</div>}
      <Titulo right={!esAgente && !puedeEditar ? <span className="au-badge au-badge--neutral" style={{ fontSize: 12 }}>Solo lectura</span>
        : puedeEditar ? <button type="button" className="au-btn au-btn--primary" onClick={() => setFormTmp({ modo: 'alta' })} style={{ height: 32, boxSizing: 'border-box', padding: '0 14px', fontSize: 12 }}>{<Ic n="PlusCircle" chico />} Agregar producto temporal</button> : null}>
        Lista de Precios{!esAgente ? ' · Sala de Ventas' : ''}
      </Titulo>
      {globoN && <GloboNota C={C} r={globoN.r} pos={globoN.pos} movil={movil} onCerrar={cerrarNota} />}{/* APP 2/28 */}
      {error && <div className="au-banner au-banner--error" style={{ marginBottom: 12 }}>{error}</div>}
      {formTmp && (
        <TemporalSalaForm C={C} modo={formTmp.modo} sku={formTmp.sku} temporalId={formTmp.temporalId} quien={quien}
          onClose={() => setFormTmp(null)}
          onGuardado={async (msg) => { showToast('ok', msg); await load(); contarPendientes(); }} />
      )}
      {!esAgente && esSuperAdmin && (
        <div style={{ marginBottom: 12 }}>
          <div className="au-toggle">
            <button type="button" aria-pressed={vista === 'lista'} onClick={() => setVista('lista')}>Lista Sala de Ventas</button>
            <button type="button" aria-pressed={vista === 'pendientes'} onClick={() => setVista('pendientes')}>{<Ic n="ClipboardList" chico />} Pendientes de biblia ({nPendientes ?? '…'})</button>
          </div>
        </div>
      )}
      {!esAgente && esSuperAdmin && vista === 'pendientes' ? (
        <PendientesBibliaSala C={C} quien={quien} onLigado={async (msg) => { showToast('ok', msg); await load(); contarPendientes(); }} />
      ) : (<>
        <Buscador C={C} value={q} onChange={setQ}
          derecha={esAgente ? `${prendidos} producto${prendidos !== 1 ? 's' : ''} · ${estrellas} ⭐` : `${total} total · ${prendidos} prendidos · ${estrellas} ⭐`}>
          <button type="button" onClick={() => descargarListaSala(visibles.filter((r) => r.activo_sala))}
            style={{ background: `${C.grad}`, color: '#fff', border: 'none', borderRadius: RADIO, height: 40, boxSizing: 'border-box', padding: '0 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--au-font-text)', whiteSpace: 'nowrap' }}>{<Ic n="FileText" chico />} Descargar PDF</button>
          {!esAgente && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.muted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <button type="button" onClick={() => setMostrarApagados((v) => !v)} className="au-switch" data-on={mostrarApagados} style={{ display: 'inline-block' }}><span className="au-switch__knob" /></button>
              Mostrar apagados
            </label>
          )}
        </Buscador>

        {visibles.length === 0 ? (
          <div className="au-glass" style={{ textAlign: 'center', padding: 40, color: C.muted }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{<Ic n="Search" chico />}</div>
            <div>{q ? `Sin resultados para "${q}"` : 'Sin productos'}</div>
          </div>
        ) : (
          <>
            {top.length > 0 && (
              <Grupo C={C} acento={C.yellow} titulo="Top" derecha={<span style={{ fontSize: 12, color: C.muted }}>{top.length} producto{top.length !== 1 ? 's' : ''}</span>}>
                {tabla(top, !ql)}
              </Grupo>
            )}
            {normales.map((g) => grupoProveedor(g, 'n'))}
            {promos.length > 0 && (
              <>
                <Titulo>Promociones</Titulo>
                {promos.map((g) => grupoProveedor(g, 'p'))}
              </>
            )}
          </>
        )}
      </>)}
    </>
  );
}
